import React from 'react';
import { Box, Flex, Paper, Stack, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { PerformanceReviewRound } from '../../../../api/generated/model/performanceReviewRound';
import { PerformanceReviewRoundStage } from '../../../../api/generated/model/performanceReviewRoundStage';
import { Eyebrow } from '../../parts/primitives';

interface ActivityItem {
  id: string;
  when: Date;
  actor: string;
  action: string;
  target: string;
  tone: 'accent' | 'neutral';
}

const formatRelativeTime = (when: Date, locale: string, t: TFunction) => {
  const diffMs = Date.now() - when.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return t('perfReview.adminHome.activity.justNow');
  if (diffMin < 60) return t('perfReview.adminHome.activity.minAgo', { n: diffMin });
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return t('perfReview.adminHome.activity.hAgo', { n: diffHr });
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return t('perfReview.adminHome.activity.dAgo', { n: diffDay });
  return when.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
};

interface RecentActivityProps {
  rounds: PerformanceReviewRound[];
  locale: string;
}

// Builds a small activity feed from rounds' stageHistory entries (the only
// activity-like data the backend currently exposes). When the backend grows
// a proper activity stream, this component can swap to that endpoint
// without changing its props shape.
export const RecentActivity: React.FC<RecentActivityProps> = ({ rounds, locale }) => {
  const { t } = useTranslation();
  const items: ActivityItem[] = [];
  for (const r of rounds) {
    const sh =
      ((r.stageHistory as unknown as Array<{
        stage?: PerformanceReviewRoundStage;
        transitionedAt?: string | Date;
      }>) ||
        []) ??
      [];
    for (const entry of sh) {
      if (!entry.stage || !entry.transitionedAt) continue;
      items.push({
        id: `${r._id}-${entry.stage}-${String(entry.transitionedAt)}`,
        when: new Date(entry.transitionedAt),
        actor: r.title || '—',
        action: t('perfReview.adminHome.activity.transitionedTo'),
        target: t(`perfReview.home.stageLabel.${entry.stage}`, { defaultValue: entry.stage }),
        tone:
          entry.stage === 'finished'
            ? 'accent'
            : entry.stage === 'collect-feedback'
              ? 'accent'
              : 'neutral',
      });
    }
  }
  items.sort((a, b) => +b.when - +a.when);
  const top = items.slice(0, 6);

  return (
    <Paper withBorder radius="lg" p="md">
      <Eyebrow>{t('perfReview.adminHome.activity.eyebrow')}</Eyebrow>
      <Title order={4} size="h5" mt={6} mb="md">
        {t('perfReview.adminHome.activity.title')}
      </Title>
      {top.length === 0 ? (
        <Text c="dimmed" size="sm">
          {t('perfReview.adminHome.activity.empty')}
        </Text>
      ) : (
        <Stack gap="md" style={{ position: 'relative' }}>
          {top.map((a, i) => (
            <Flex key={a.id} gap="sm" align="flex-start" style={{ position: 'relative' }}>
              {i < top.length - 1 && (
                <Box
                  style={{
                    position: 'absolute',
                    left: 5,
                    top: 16,
                    bottom: -16,
                    width: 2,
                    background: 'var(--mantine-color-default-border)',
                  }}
                />
              )}
              <Box
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 999,
                  background:
                    a.tone === 'accent'
                      ? 'var(--mantine-primary-color-filled)'
                      : 'var(--mantine-color-default)',
                  border:
                    '2px solid ' +
                    (a.tone === 'accent'
                      ? 'var(--mantine-primary-color-filled)'
                      : 'var(--mantine-color-default-border)'),
                  marginTop: 4,
                  flexShrink: 0,
                  zIndex: 1,
                }}
              />
              <Box style={{ minWidth: 0, flex: 1 }}>
                <Text size="sm" lh={1.4}>
                  <Text component="span" fw={500} inherit>
                    {a.actor}
                  </Text>{' '}
                  {a.action}{' '}
                  <Text component="span" fw={500} inherit>
                    {a.target}
                  </Text>
                </Text>
                <Text size="xs" c="dimmed" mt={2} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatRelativeTime(a.when, locale, t)}
                </Text>
              </Box>
            </Flex>
          ))}
        </Stack>
      )}
    </Paper>
  );
};
